import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '@/shared/components/form';
import { lookupDistance } from '../../api';
import type { DistanceResult } from '../../types';
import { EditorSection } from './EditorSection';
import { NumberField } from './NumberField';
import type { SectionProps } from './types';

type Lookup = { state: 'idle' | 'looking' } | { state: 'done'; status: DistanceResult['status'] | 'error' };

/** Site address → driving distance. The lookup never blocks drafting: km stays editable (spec § Failure handling). */
export function SiteSection({ draft, dispatch, readOnly, errors }: SectionProps) {
  const { t } = useTranslation('crm');
  const [lookup, setLookup] = useState<Lookup>({ state: 'idle' });
  const [addressEdited, setAddressEdited] = useState(false);
  const { inputs } = draft;

  const findDistance = async () => {
    const address = draft.siteAddress.trim();
    if (!address) return;
    setLookup({ state: 'looking' });
    try {
      const result = await lookupDistance(address);
      if (result.status === 'ok' && result.km !== null) dispatch({ type: 'setInputs', patch: { km: result.km } });
      setLookup({ state: 'done', status: result.status });
    } catch {
      setLookup({ state: 'done', status: 'error' });
    }
  };

  return (
    <EditorSection title={t('estimates.site.title')}>
      <FormField label={t('estimates.site.address')} htmlFor="est-site-address" error={errors.siteAddress}>
        <div className="flex gap-2">
          <Input
            id="est-site-address"
            value={draft.siteAddress}
            maxLength={500}
            disabled={readOnly}
            onChange={(e) => { setAddressEdited(true); dispatch({ type: 'setField', field: 'siteAddress', value: e.target.value }); }}
            onBlur={() => { if (addressEdited) { setAddressEdited(false); void findDistance(); } }}
          />
          <Button
            type="button"
            variant="outline"
            disabled={readOnly || !draft.siteAddress.trim() || lookup.state === 'looking'}
            onClick={() => void findDistance()}
          >
            <MapPin className="h-4 w-4 mr-1" /> {lookup.state === 'looking' ? t('estimates.site.lookingUp') : t('estimates.site.lookUp')}
          </Button>
        </div>
      </FormField>
      {lookup.state === 'done' && (
        <p role="status" className={lookup.status === 'ok' ? 'text-xs text-muted-foreground' : 'text-xs text-warning-dark'}>
          {t(`estimates.site.lookup.${lookup.status}`)}
        </p>
      )}
      <div className="grid gap-4 sm:grid-cols-3">
        <NumberField
          id="est-km" label={t('estimates.site.km')} hint={t('estimates.site.kmHint')} value={inputs.km} allowEmpty max={20000}
          disabled={readOnly} error={errors['inputs.km']} onChange={(km) => dispatch({ type: 'setInputs', patch: { km } })}
        />
        <NumberField
          id="est-calibration-visits" label={t('estimates.site.calibrationVisits')} value={inputs.calibrationVisitsPerYear} max={365}
          disabled={readOnly} error={errors['inputs.calibrationVisitsPerYear']}
          onChange={(v) => dispatch({ type: 'setInputs', patch: { calibrationVisitsPerYear: v ?? 0 } })}
        />
        <NumberField
          id="est-calibration-shipments" label={t('estimates.site.calibrationShipments')} value={inputs.calibrationShipmentsPerYear} max={365}
          disabled={readOnly} error={errors['inputs.calibrationShipmentsPerYear']}
          onChange={(v) => dispatch({ type: 'setInputs', patch: { calibrationShipmentsPerYear: v ?? 0 } })}
        />
      </div>
    </EditorSection>
  );
}
